<template>
  <div class="flex flex-col gap-3 min-h-[200px]">
    <!-- Admin mode -->
    <div v-if="admin_view" class="flex flex-col gap-3">
      <button class="text-xs text-text-link self-start" @click="admin_view = false">{{ $t('scheduled.tasks.backToMyTasks') }}</button>
      <scheduled-admin @close="$emit('close')" />
    </div>

    <!-- Approvals mode -->
    <div v-else-if="approvals_view" class="flex flex-col gap-3">
      <button class="text-xs text-text-link self-start" @click="approvals_view = false">{{ $t('scheduled.tasks.backToMyTasks') }}</button>
      <action-approval-queue />
    </div>

    <!-- Results inbox mode (NPM-19) -->
    <div v-else-if="inbox_view" class="flex flex-col gap-3">
      <button class="text-xs text-text-link self-start" @click="closeInbox">{{ $t('scheduled.tasks.backToMyTasks') }}</button>
      <scheduled-inbox :task-id="inbox_task_id" :filter-task-name="inbox_task_name" @clear-task="inbox_task_id = ''" />
    </div>

    <template v-else>
    <!-- AI schedule suggestion (opt-in; self-hides when there's none) -->
    <schedule-suggestion-chip @created="onSuggestionAccepted" />

    <!-- The standing line the design doc asks for: a task can be set up here or asked for from any
         topic. It states where results go, so nobody has to create one to find out. -->
    <div class="flex items-start gap-2">
      <div class="text-xs text-text-subtle flex-1" data-test="tasks-intro">
        {{ $t('scheduled.tasks.intro') }}
      </div>
    </div>

    <!-- Create / edit form. Editing an existing task still takes over the panel: it is a different job
         from creating one, it is reached from a specific row, and the row it belongs to is the context
         the person already has. -->
    <div v-if="editing" class="flex flex-col gap-3">
      <scheduled-edit ref="editor" :task="editing_task" @save="onSaved" @cancel="closeEditor" />
      <div class="flex items-center justify-end gap-2">
        <button class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link" @click="closeEditor">{{ $t('common.cancel') }}</button>
        <button class="text-xs px-2 py-1 rounded bg-btn-primary text-white" @click="saveEditor">{{ $t('scheduled.tasks.save') }}</button>
      </div>
    </div>

    <template v-else>

    <!-- Creating sits above the list rather than behind a button that replaces it, so the person can see
         what they already own while adding to it. -->
    <scheduled-create
      :disabled="create_disabled"
      :disabled-reason="create_disabled_reason"
      @created="onCreated"
    />

    <!-- The morning brief opts in here rather than through the create form: it is the one task type
         with nothing to fill in, so a toggle and a time is the whole of it. Below the create
         accordion and above the list, because it is neither — not something authored here, and not
         one of the tasks the list enumerates (tasks_sorted filters it out). -->
    <morning-brief-opt-in
      :disabled="create_disabled"
      :disabled-reason="create_disabled_reason"
      @changed="onBriefChanged"
    />

    <!-- Shown in every state, not only once there are tasks: Approvals and Admin view live here, and an
         empty or still-loading list must not leave them unreachable. Results too — runs outlive the
         tasks that produced them. -->
    <div class="flex items-center gap-2 mt-4" data-test="tasks-header">
      <span class="text-[11px] uppercase tracking-wide text-text-subtle flex-1">Your tasks</span>
      <!-- The count states active against total, so a paused task stays visible and countable rather
           than looking as though it has gone. -->
      <span v-if="tasks_sorted.length" class="text-xs text-text-subtle">{{ active_count }} active of {{ tasks_sorted.length }}<span v-if="task_cap" data-test="tasks-cap"> · Limit {{ task_cap }}</span></span>
      <!-- @click.stop keeps clicks inside the menu from reaching the document listener that closes it. -->
      <div ref="overflow_wrap" class="relative flex-shrink-0" @click.stop>
        <button
          type="button"
          class="h-7 w-7 flex items-center justify-center rounded hover:bg-surface-3 text-text-link"
          title="More"
          aria-label="More"
          aria-haspopup="menu"
          :aria-expanded="overflow_open ? 'true' : 'false'"
          data-test="tasks-menu"
          @click="toggleOverflow"
        >
          <i class="ri-more-2-fill ri-125" />
        </button>
        <div
          v-if="overflow_open"
          role="menu"
          class="absolute right-0 top-8 z-20 min-w-[10rem] bg-surface-1 border border-border-separator rounded-md shadow-lg flex flex-col py-1"
        >
          <button
            type="button"
            role="menuitem"
            class="text-xs text-left px-3 py-2 text-text-default hover:bg-surface-2"
            @click="openOverflow('results')"
          >{{ $t('scheduled.tasks.results') }}</button>
          <button
            v-if="approvals_available"
            type="button"
            role="menuitem"
            class="text-xs text-left px-3 py-2 text-text-default hover:bg-surface-2"
            @click="openOverflow('approvals')"
          >{{ $t('scheduled.tasks.approvals') }}</button>
          <button
            v-if="is_fusion_admin"
            type="button"
            role="menuitem"
            class="text-xs text-left px-3 py-2 text-text-default hover:bg-surface-2"
            @click="openOverflow('admin')"
          >{{ $t('scheduled.tasks.adminView') }}</button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="scheduled_tasks_processing && !tasks_sorted.length" class="px-2 py-6 text-sm text-text-subtle">
      {{ $t('scheduled.tasks.loading') }}
    </div>

    <!-- Empty -->
    <div v-else-if="!tasks_sorted.length" class="px-2 py-8 text-center text-sm text-text-subtle">
      {{ $t('scheduled.tasks.empty') }}
    </div>

    <!-- List -->
    <template v-else>
      <div class="flex flex-col gap-2">
        <scheduled-task-row
          v-for="task in tasks_sorted"
          :key="task.id"
          :task="task"
          :busy="isRowBusy(task.id)"
          :beta-closed="beta_closed"
          :is-read-only="is_read_only"
          :history-open="history_open_id === task.id"
          :history-runs="history_runs"
          :history-audit="history_audit"
          :history-processing="history_processing"
          @toggle-enabled="toggleEnabled"
          @run-now="runNow"
          @edit="startEdit"
          @toggle-history="toggleHistory"
          @delete="deleteTask"
          @open-results="openTaskResults"
        />
      </div>
    </template>

    <!-- Absolute, not fixed: nothing between here and DialogModal's panel is positioned, so the toast
         pins to the modal's top-right (below its header) and stays put while the content scrolls. -->
    <transition name="row-toast">
      <div
        v-if="row_message"
        :role="row_message_is_error ? 'alert' : 'status'"
        class="absolute top-14 right-3 z-20 w-[280px] max-w-[calc(100%-1.5rem)] rounded-lg border text-xs shadow-lg px-3 py-2.5"
        :class="row_message_is_error ? 'bg-[var(--background-error)] border-[var(--border-error)] text-danger' : 'bg-blue-100 border-blue-800 text-blue-800'"
        data-test="row-toast"
      >{{ row_message }}</div>
    </transition>
    </template>
    </template>
  </div>
</template>

<style scoped>
  .row-toast-enter-active,
  .row-toast-leave-active {
    transition: opacity .5s ease, transform .5s ease;
  }
  .row-toast-enter-from,
  .row-toast-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
</style>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import ScheduledEdit from '@components/Scheduled/ScheduledEdit.vue';
import ScheduledAdmin from '@components/Scheduled/ScheduledAdmin.vue';
import ActionApprovalQueue from '@components/Scheduled/ActionApprovalQueue.vue';
import ScheduledInbox from '@components/Scheduled/ScheduledInbox.vue';
import ScheduleSuggestionChip from '@components/Scheduled/ScheduleSuggestionChip.vue';
import ScheduledCreate from '@components/Scheduled/ScheduledCreate.vue';
import MorningBriefOptIn from '@components/Scheduled/MorningBriefOptIn.vue';
import ScheduledTaskRow from '@components/Scheduled/ScheduledTaskRow.vue';

export default {
  name: 'ScheduledManagement',
  props: {
    /** 'inbox' opens straight on the results list. Anything else opens on the task list. */
    initialView: {
      type: String,
      required: false,
      default: '',
    },
  },
  components: {
    'scheduled-edit': ScheduledEdit,
    'scheduled-admin': ScheduledAdmin,
    'action-approval-queue': ActionApprovalQueue,
    'scheduled-inbox': ScheduledInbox,
    'schedule-suggestion-chip': ScheduleSuggestionChip,
    'scheduled-create': ScheduledCreate,
    'morning-brief-opt-in': MorningBriefOptIn,
    'scheduled-task-row': ScheduledTaskRow,
  },
  emits: ['close'],
  created() {
    this.getScheduledTasks();
    this.getScheduledAvailability();
  },
  beforeUnmount() {
    // closeOverflow is what removes the document listener, and the menu can be open when the dialog closes.
    this.closeOverflow();
  },
  data() {
    return {
      busy_ids: [],
      row_message: '',
      row_message_is_error: false,
      editing: false,
      editing_task: null,
      history_open_id: null,
      history_runs: [],
      history_audit: [],
      history_processing: false,
      admin_view: false,
      approvals_view: false,
      overflow_open: false,
      // Seeded from initialView so the thread's "See all results" can land on the results list
      // rather than on the task list with the user left to find it. Read once, in data(), because
      // after that the view is the user's to change — a prop that kept forcing it would fight the
      // back control.
      inbox_view: this.initialView === 'inbox',
      // Which task the inbox is narrowed to, or '' for everything. Set by a row's "Open results" and
      // cleared when the inbox is left, so returning to it by the panel-level control never reopens
      // somebody's last row filter as though it were the whole list.
      inbox_task_id: '',
    };
  },
  computed: {
    ...mapStores(useMainStore),
    is_read_only() {
      return this.mainStore.is_read_only;
    },
    is_fusion_admin() {
      return this.mainStore.is_fusion_admin;
    },
    // The store this panel is showing, so the availability question is asked about it rather than about
    // whichever rooftop happens to be first in the caller's associations.
    current_cif() {
      return this.mainStore.current_cif;
    },
    scheduled_tasks: {
      get() { return this.mainStore.scheduled_tasks; },
      set(value) { this.mainStore.scheduled_tasks = value; },
    },
    scheduled_tasks_processing: {
      get() { return this.mainStore.scheduled_tasks_processing; },
      set(value) { this.mainStore.scheduled_tasks_processing = value; },
    },
    scheduled_availability: {
      get() { return this.mainStore.scheduled_availability; },
      set(value) { this.mainStore.scheduled_availability = value; },
    },
    scheduled_availability_generation() {
      return this.mainStore.scheduled_availability_generation;
    },
    // Only ever true once the server has actually said so. A pending or failed lookup leaves this false
    // on purpose: telling someone their store is excluded before we know it is would make every panel
    // load flash a refusal, and the write paths refuse on their own regardless.
    beta_closed() {
      return !!this.scheduled_availability && this.scheduled_availability.enabled === false;
    },
    /**
     * Whether the approvals queue is reachable for this store.
     *
     * Answered by the availability endpoint rather than by asking the allowlist directly, because the
     * allowlist route is admin-only — an ordinary owner gets a 403 asking about their own rooftop, so
     * this surface could never have gated itself on it. Absent ⇒ false: a store with no approved action
     * types cannot produce a draft, so the queue it hides is one that could only ever have been empty.
     */
    approvals_available() {
      return !!(this.scheduled_availability && this.scheduled_availability.approvals);
    },
    // Creating is withdrawn on a closed rooftop and for a read-only caller, but the control stays
    // on screen and says why. Hiding it would leave the page looking complete while silently missing
    // the thing the person came to do.
    create_disabled() {
      return this.beta_closed || this.is_read_only;
    },
    create_disabled_reason() {
      // There is no per-store opt-in any more: the server answers closed only when this deployment does
      // not offer scheduled tasks, or there is no store to create one for. Neither is something a store
      // administrator can switch on, so the sentence does not send anyone to one.
      if (this.beta_closed) {
        return "Scheduled tasks aren't available right now. "
          + 'Anything already scheduled is listed below and can still be paused or removed.';
      }
      if (this.is_read_only) return 'Your account has read-only access, so creating a task is unavailable.';
      return '';
    },
    /**
     * The list, minus the morning brief.
     *
     * The brief has its own card at the top of this panel, which owns opting in, the schedule and the
     * pause. Leaving it in the list as well would put two enable switches for one object on a single
     * scroll — and the row's Delete would sit beside a toggle that deliberately pauses instead, so the
     * two controls would not even agree on what turning it off means.
     */
    tasks_sorted() {
      const tasks = Array.isArray(this.scheduled_tasks) ? this.scheduled_tasks : [];
      return tasks
        .filter(t => t && t.taskType !== 'MorningBrief')
        .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    },
    active_count() {
      return this.tasks_sorted.filter(t => t.enabled).length;
    },
    // The per-user cap, from the availability answer. Null until the server has said, so an older
    // server or a failed lookup shows the count alone rather than "Limit undefined". It is checked against
    // the same tasks tasks_sorted lists: paused ones count, the morning brief does not.
    task_cap() {
      const cap = this.scheduled_availability && this.scheduled_availability.taskCap;
      return typeof cap === 'number' && cap > 0 ? cap : null;
    },
    // Resolved here rather than in the inbox so the banner names the task even after it has been
    // deleted from under the filter — the runs outlive the task, and so should the label on them.
    inbox_task_name() {
      if (!this.inbox_task_id) return '';
      const task = this.tasks_sorted.find(t => t.id === this.inbox_task_id);
      return (task && task.name) || '';
    },
  },
  methods: {
    getScheduledAvailability() {
      const self = this;
      // The generation this reply belongs to; see the note in OptionsMenu for why it is a counter and not
      // a comparison of stores. This panel needs the check as much as the menu does — the field is shared,
      // so a reply that outlived its caller or rooftop does not merely mislead this surface, it clobbers
      // the menu's verdict, where a stale truthy answer is never re-asked.
      const generation = this.scheduled_availability_generation;
      this.mainStore.GetScheduledAvailability(this.current_cif).then((response) => {
        if (generation !== self.scheduled_availability_generation) return;
        // Only a well-formed answer counts. Anything else leaves availability unknown rather than
        // guessing a refusal out of a shape we did not recognise.
        self.scheduled_availability = response && typeof response.enabled === 'boolean' ? response : null;
      }, () => {
        if (generation !== self.scheduled_availability_generation) return;
        self.scheduled_availability = null;
      });
    },
    getScheduledTasks() {
      if (this.scheduled_tasks_processing) return;
      const self = this;
      this.scheduled_tasks_processing = true;
      this.mainStore.GetScheduledTasks().then((response) => {
        self.scheduled_tasks = Array.isArray(response) ? response : (response && response.tasks) || [];
        self.scheduled_tasks_processing = false;
      }, () => {
        self.scheduled_tasks_processing = false;
      });
    },
    toggleOverflow() {
      // Same open/close mechanics as ReyConversationList's row menu: the document listener is added on
      // the next tick so the click that opened the menu does not immediately close it.
      if (this.overflow_open) {
        this.closeOverflow();
        return;
      }
      this.overflow_open = true;
      const self = this;
      this.$nextTick(() => {
        // Capture phase so a click anywhere closes the menu even if an ancestor stops propagation.
        document.addEventListener('click', self.onDocumentClick, true);
      });
    },
    onDocumentClick(event) {
      // Clicks inside the menu wrapper are handled by the button/items themselves.
      const wrap = this.$refs.overflow_wrap;
      if (wrap && wrap.contains(event.target)) return;
      this.closeOverflow();
    },
    closeOverflow() {
      this.overflow_open = false;
      document.removeEventListener('click', this.onDocumentClick, true);
    },
    // Every item closes the menu first, so it is never left floating over the view it just opened.
    openOverflow(which) {
      this.closeOverflow();
      if (which === 'results') this.openAllResults();
      if (which === 'approvals') this.approvals_view = true;
      if (which === 'admin') this.admin_view = true;
    },
    openAllResults() {
      this.inbox_task_id = '';
      this.inbox_view = true;
    },
    openTaskResults(task) {
      this.inbox_task_id = task.id;
      this.inbox_view = true;
    },
    closeInbox() {
      this.inbox_view = false;
      this.inbox_task_id = '';
    },
    startEdit(task) {
      this.editing_task = task;
      this.editing = true;
    },
    closeEditor() {
      this.editing = false;
      this.editing_task = null;
    },
    saveEditor() {
      if (this.$refs.editor) this.$refs.editor.save();
    },
    onSaved() {
      this.closeEditor();
      this.getScheduledTasks();
      this.flashMessage(this.$t('scheduled.tasks.savedToast'));
    },
    onCreated() {
      this.getScheduledTasks();
      this.flashMessage('Task created — Rey will run it on the schedule you set.');
    },
    onSuggestionAccepted() {
      this.getScheduledTasks();
      this.flashMessage(this.$t('scheduled.tasks.suggestionAcceptedToast'));
    },
    /**
     * The brief card changed something. Refetched rather than trusted, for the reason onCreated does
     * it: the server recomputes NextRunAt from the trigger, so the times this panel shows are only
     * right after a read.
     *
     * A pause is the exception — the card already updated the task in place, and refetching is still
     * cheaper than working out which of the four outcomes needs one.
     */
    onBriefChanged(what) {
      this.getScheduledTasks();
      if (what === 'created') {
        this.flashMessage('Morning brief on — Rey will have it waiting for you.');
        return;
      }
      if (what === 'paused') {
        this.flashMessage('Morning brief off. Nothing already delivered is removed.');
        return;
      }
      this.flashMessage(what === 'resumed' ? 'Morning brief on.' : 'Morning brief updated.');
    },
    toggleHistory(task) {
      if (this.history_open_id === task.id) {
        this.history_open_id = null;
        return;
      }
      const self = this;
      this.history_open_id = task.id;
      this.history_runs = [];
      this.history_audit = [];
      this.history_processing = true;
      Promise.all([
        this.mainStore.GetScheduledTaskRuns(task.id).then(
          r => { self.history_runs = Array.isArray(r) ? r : (r && r.runs) || []; },
          () => { self.history_runs = []; }),
        this.mainStore.GetScheduledTaskAudit(task.id).then(
          r => { self.history_audit = Array.isArray(r) ? r : (r && r.entries) || []; },
          () => { self.history_audit = []; }),
      ]).then(() => { self.history_processing = false; });
    },
    toggleEnabled(task) {
      if (this.isRowBusy(task.id)) return;
      const self = this;
      const nextEnabled = !task.enabled;
      this.setRowBusy(task.id, true);
      this.mainStore.SetScheduledTaskEnabled({ id: task.id, enabled: nextEnabled }).then(() => {
        task.enabled = nextEnabled;
        self.setRowBusy(task.id, false);
        self.flashMessage(nextEnabled ? this.$t('scheduled.tasks.resumedToast') : this.$t('scheduled.tasks.pausedToast'));
      }, (err) => {
        self.setRowBusy(task.id, false);
        // A one-time task whose moment passed while it was paused: the server says why, and "try again" would
        // be wrong advice. The list is re-read so the row — rendered while that moment was still ahead —
        // picks up that it is now expired and drops its switch.
        if (err && err.code === 'one_time_spent') {
          self.flashMessage(self.$t('scheduled.tasks.oneTimeSpentToast'), true);
          self.getScheduledTasks();
          return;
        }
        self.flashMessage(self.$t('scheduled.tasks.updateFailedToast'), true);
      });
    },
    runNow(task) {
      if (this.isRowBusy(task.id)) return;
      const self = this;
      this.setRowBusy(task.id, true);
      this.mainStore.RunScheduledTaskNow(task.id).then(() => {
        self.setRowBusy(task.id, false);
        self.flashMessage(self.$t('scheduled.tasks.queuedToast'));
      }, () => {
        self.setRowBusy(task.id, false);
        self.flashMessage(self.$t('scheduled.tasks.runFailedToast'), true);
      });
    },
    deleteTask(task) {
      const self = this;
      this.setRowBusy(task.id, true);
      this.mainStore.DeleteScheduledTask(task.id).then(() => {
        self.scheduled_tasks = self.scheduled_tasks.filter(t => t.id !== task.id);
        self.setRowBusy(task.id, false);
        self.flashMessage(self.$t('scheduled.tasks.deletedToast'));
      }, () => {
        self.setRowBusy(task.id, false);
        self.flashMessage(self.$t('scheduled.tasks.deleteFailedToast'), true);
      });
    },
    isRowBusy(id) {
      return this.busy_ids.includes(id);
    },
    setRowBusy(id, busy) {
      if (busy) {
        if (!this.busy_ids.includes(id)) this.busy_ids.push(id);
      } else {
        this.busy_ids = this.busy_ids.filter(x => x !== id);
      }
    },
    flashMessage(msg, isError = false) {
      const self = this;
      this.row_message = msg;
      this.row_message_is_error = isError;
      setTimeout(() => { if (self.row_message === msg) self.row_message = ''; }, 3000);
    },
    invalidateScheduledAvailability() {
      this.mainStore.InvalidateScheduledAvailability();
    },
  },
  watch: {
    // Availability is answered per rooftop, so a switch makes the held answer about another store. The
    // options menu watches this too, and this panel is only ever opened from there — but inheriting the
    // invalidation from a sibling that merely happens to be mounted is the kind of hidden coupling that
    // breaks the moment either surface moves. Each writer of the shared field invalidates for itself.
    current_cif() {
      this.invalidateScheduledAvailability();
    },
  },
};
</script>
